import React, { useState, useEffect, useRef } from 'react';
import '../styles/LoveGame.css';

const LoveGame = () => {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [message, setMessage] = useState('');
  const [gameStarted, setGameStarted] = useState(false);
  const gameStateRef = useRef({
    player: { x: 400, y: 300, radius: 20, vx: 0, vy: 0 },
    hearts: [],
    keys: {},
    score: 0,
    collectedHearts: [],
  });

  const loveMessages = [
    'I love you so much! 💕',
    'You make me so happy! 😊',
    'You\'re my favorite person! ⭐',
    'I\'m so lucky to have you! 🍀',
    'You\'re amazing! 💫',
    'My heart belongs to you! 💗',
    'You\'re my everything! 🌟',
    'I adore you! 😍',
    'You\'re the best! 🏆',
    'Forever and always! 💕',
    'You\'re my soulmate! 👫',
    'I\'m so in love with you! 💕',
    'You make my heart skip a beat! 💓',
    'You\'re my sunshine! ☀️',
    'I choose you every day! 💑',
  ];

  const gameState = gameStateRef.current;

  // Initialize hearts on canvas
  const initializeHearts = () => {
    const hearts = [];
    for (let i = 0; i < 15; i++) {
      hearts.push({
        x: Math.random() * 750 + 25,
        y: Math.random() * 550 + 25,
        radius: 12,
        collected: false,
        id: i,
      });
    }
    gameStateRef.current.hearts = hearts;
  };

  // Handle keyboard input
  useEffect(() => {
    const handleKeyDown = (e) => {
      gameState.keys[e.key.toLowerCase()] = true;
    };

    const handleKeyUp = (e) => {
      gameState.keys[e.key.toLowerCase()] = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [gameState]);

  // Draw function
  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.fillStyle = '#fff5f7';
    ctx.fillRect(0, 0, 800, 600);

    // Draw border
    ctx.strokeStyle = '#ff69b4';
    ctx.lineWidth = 3;
    ctx.strokeRect(0, 0, 800, 600);

    // Draw player circle
    ctx.fillStyle = '#ff69b4';
    ctx.beginPath();
    ctx.arc(gameState.player.x, gameState.player.y, gameState.player.radius, 0, Math.PI * 2);
    ctx.fill();

    // Draw player glow
    ctx.strokeStyle = 'rgba(255, 105, 180, 0.3)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(gameState.player.x, gameState.player.y, gameState.player.radius + 5, 0, Math.PI * 2);
    ctx.stroke();

    // Draw hearts
    gameState.hearts.forEach((heart) => {
      if (!heart.collected) {
        // Draw heart shape
        drawHeart(ctx, heart.x, heart.y, heart.radius);
        
        // Draw heart glow
        ctx.strokeStyle = 'rgba(255, 165, 0, 0.3)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(heart.x, heart.y, heart.radius + 8, 0, Math.PI * 2);
        ctx.stroke();
      }
    });

    // Draw score
    ctx.fillStyle = '#ff1493';
    ctx.font = 'bold 20px Arial';
    ctx.fillText(`Hearts Collected: ${gameState.score}`, 20, 30);

    // Draw message
    if (message) {
      ctx.fillStyle = 'rgba(255, 105, 180, 0.9)';
      ctx.font = 'bold 16px Arial';
      const textWidth = ctx.measureText(message).width;
      ctx.fillText(message, 400 - textWidth / 2, 560);
    }
  };

  // Draw heart shape
  const drawHeart = (ctx, x, y, size) => {
    ctx.fillStyle = '#ff4500';
    ctx.beginPath();
    ctx.moveTo(x, y + size / 2);
    ctx.bezierCurveTo(x - size / 2, y - size / 2, x - size, y, x - size / 2, y - size / 3);
    ctx.bezierCurveTo(x - size / 4, y - size / 2, x, y - size / 2, x, y);
    ctx.bezierCurveTo(x, y - size / 2, x + size / 4, y - size / 2, x + size / 2, y - size / 3);
    ctx.bezierCurveTo(x + size, y, x + size / 2, y - size / 2, x, y + size / 2);
    ctx.fill();
  };

  // Update game state
  const update = () => {
    // Update player velocity based on keys
    gameState.player.vx = 0;
    gameState.player.vy = 0;
    const speed = 5;

    if (gameState.keys['w'] || gameState.keys['arrowup']) gameState.player.vy = -speed;
    if (gameState.keys['s'] || gameState.keys['arrowdown']) gameState.player.vy = speed;
    if (gameState.keys['a'] || gameState.keys['arrowleft']) gameState.player.vx = -speed;
    if (gameState.keys['d'] || gameState.keys['arrowright']) gameState.player.vx = speed;

    // Update player position
    gameState.player.x += gameState.player.vx;
    gameState.player.y += gameState.player.vy;

    // Boundary collision
    const padding = gameState.player.radius;
    if (gameState.player.x - padding < 0) gameState.player.x = padding;
    if (gameState.player.x + padding > 800) gameState.player.x = 800 - padding;
    if (gameState.player.y - padding < 0) gameState.player.y = padding;
    if (gameState.player.y + padding > 600) gameState.player.y = 600 - padding;

    // Check heart collisions
    gameState.hearts.forEach((heart) => {
      if (!heart.collected) {
        const dx = gameState.player.x - heart.x;
        const dy = gameState.player.y - heart.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < gameState.player.radius + heart.radius) {
          heart.collected = true;
          gameState.score += 1;
          setScore(gameState.score);

          // Show random love message
          const randomMessage = loveMessages[Math.floor(Math.random() * loveMessages.length)];
          setMessage(randomMessage);

          // Clear message after 2 seconds
          setTimeout(() => setMessage(''), 2000);
        }
      }
    });

    // Check if all hearts collected
    if (gameState.hearts.every((h) => h.collected)) {
      setMessage('You collected all my love! 💕💕💕');
    }
  };

  // Game loop
  useEffect(() => {
    if (!gameStarted) return;

    const gameLoop = () => {
      update();
      draw();
    };

    const interval = setInterval(gameLoop, 1000 / 60); // 60 FPS
    return () => clearInterval(interval);
  }, [gameStarted, message]);

  const handleStart = () => {
    initializeHearts();
    setGameStarted(true);
    setScore(0);
    setMessage('');
    gameStateRef.current.player = { x: 400, y: 300, radius: 20, vx: 0, vy: 0 };
    gameStateRef.current.score = 0;
  };

  const handleReset = () => {
    setGameStarted(false);
    setScore(0);
    setMessage('');
    gameStateRef.current.player = { x: 400, y: 300, radius: 20, vx: 0, vy: 0 };
    gameStateRef.current.score = 0;
    gameStateRef.current.hearts = [];
  };

  return (
    <div className="love-game-container">
      <div className="love-game-header">
        <h1>💕 Love Game 💕</h1>
        <p className="love-game-subtitle">Collect all the hearts to see how much I love you!</p>
      </div>

      <div className="love-game-content">
        <canvas
          ref={canvasRef}
          width={800}
          height={600}
          className="love-game-canvas"
        />
      </div>

      <div className="love-game-controls">
        {!gameStarted ? (
          <button className="love-game-btn start-btn" onClick={handleStart}>
            Start Game 💕
          </button>
        ) : (
          <>
            <div className="controls-info">
              <p><strong>Use WASD or Arrow Keys to move</strong></p>
              <p>Collect all the ❤️ hearts!</p>
            </div>
            <button className="love-game-btn reset-btn" onClick={handleReset}>
              Restart Game
            </button>
          </>
        )}
      </div>

      <div className="love-game-footer">
        <p>Made with 💕 for my love</p>
      </div>
    </div>
  );
};

export default LoveGame;
